import React, { useEffect, useState } from 'react';
import styles from '../style/BlogDetail.module.css';
import { useGetBlogsQuery, useGetCommentsByBlogIdQuery, useSubmitCommentMutation, useGetBlogBySlugQuery } from '../redux/api';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Calendar, User, Clock, ChevronRight, Facebook, Twitter, Linkedin, MessageCircle, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import NotFound from './NotFound';
import { useToast } from '../components/ToastContext';

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    const [commentData, setCommentData] = useState({
        name: '',
        email: '',
        content: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [subscriberEmail, setSubscriberEmail] = useState("");
    const { showToast } = useToast();

    const { data, isLoading, error } = useGetBlogBySlugQuery(slug, {
        skip: !slug,
    });

    const { data: allBlogsRaw } = useGetBlogsQuery(1);
    const allBlogs = Array.isArray(allBlogsRaw) ? allBlogsRaw : (allBlogsRaw?.blogs || []);
    const id = data?._id;
    const { data: comments, isLoading: isCommentsLoading } = useGetCommentsByBlogIdQuery(id, {
        skip: !id
    });
    const [submitComment, { isLoading: isSubmitting }] = useSubmitCommentMutation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCommentData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await submitComment({ ...commentData, blogId: id }).unwrap();
            setIsSubmitted(true);
            setCommentData({ name: '', email: '', content: '' });
            setTimeout(() => setIsSubmitted(false), 5000);
        } catch (err) {
            alert(err.data?.message || "Failed to post comment");
        }
    };

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL || "https://api.createchsquads.com"}/api/subscribers`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: subscriberEmail }),
            });
            const resData = await res.json();
            if (res.ok) {
                showToast("Success! Welcome to our list.", "success");
                setSubscriberEmail("");
            } else {
                showToast(resData.message || "Something went wrong.", "error");
            }
        } catch (err) {
            showToast("Failed to subscribe. Please try again.", "error");
        }
    };

    const handleShare = (platform) => {
        const url = window.location.href;
        const shareTitle = data.title;
        let shareUrl = "";

        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(url)}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
                break;
            case 'whatsapp':
                shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + " " + url)}`;
                break;
            default:
                return;
        }
        window.open(shareUrl, '_blank', 'width=600,height=400');
    };

    if (isLoading) return (
        <div className={styles.loaderContainer}>
            <motion.div
                className={styles.loader}
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
        </div>
    );
    if (error || (!isLoading && !data)) return <NotFound />;


    return (
        <div className={styles.wrapper}>
            <SEO
                title={data.title}
                description={data.metaDescription || data.shortDescription || `Read "${data.title}" on CreaTech Squads blog.`}
                keywords={data.metaKeywords || `${data.title}, CreaTech Squads`}
                canonical={`/blog/${slug}`}
                ogType="article"
                ogImage={data.images?.[0] ? `${process.env.REACT_APP_API_URL || "https://api.createchsquads.com"}/${data.images[0]}` : undefined}
                author={data.authorName}
                publishedAt={data.createdAt}
                breadcrumbs={[
                    { name: "Home", item: "/" },
                    { name: "Blog", item: "/blogs" },
                    { name: data.title, item: `/blog/${slug}` }
                ]}
                schemaData={{
                    "@context": "https://schema.org",
                    "@type": "BlogPosting",
                    "headline": data.title,
                    "image": data.images?.[0] ? `${process.env.REACT_APP_API_URL || "https://api.createchsquads.com"}/${data.images[0]}` : undefined,
                    "author": {
                        "@type": "Organization",
                        "name": "CreaTech Squads"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "CreaTech Squads",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://createchsquads.com/assets/logo1.png"
                        }
                    },
                    "datePublished": data.createdAt,
                    "description": data.shortDescription
                }}
            />
            <motion.div className={styles.progressBar} style={{ scaleX }} />

            <div className={styles.navBar}>
                <button onClick={() => navigate(-1)} className={styles.backBtn}>
                    <ArrowLeft size={20} /> Back to Blogs
                </button>
            </div>

            <div className={styles.mainContainer}>
                <header className={styles.header}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className={styles.metaTop}
                    >
                        <span className={styles.categoryBadge}>Digital Insights</span>
                        <span className={styles.dot}></span>
                        <span className={styles.readTime}>
                            <Clock size={14} /> {data.timeDuration || "5 min read"}
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className={styles.title}
                    >
                        {data.title}
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className={styles.authorMeta}
                    >
                        <div className={styles.authorImg}>
                            <User size={20} />
                        </div>
                        <div className={styles.authorInfo}>
                            <span className={styles.authorName}>By {data.authorName}</span>
                            <span className={styles.postDate}>
                                <Calendar size={14} /> {new Date(data.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </span>
                        </div>
                    </motion.div>
                </header>

                {data.images && data.images.length > 0 && (
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        className={styles.featuredImageContainer}
                    >
                        <img 
                            src={`${process.env.REACT_APP_API_URL || "https://api.createchsquads.com"}/${data.images[0].replace(/\\/g, "/")}`} 
                            alt={data.title} 
                            className={styles.featuredImage} 
                        />
                        <div className={styles.imageOverlay}></div>
                        {data.imageText && (
                            <div className={styles.imageCaption}>
                                {data.imageText}
                            </div>
                        )}
                    </motion.div>
                )}

                {data.isCustomCSSEnabled && data.customCSS && (
                    <style dangerouslySetInnerHTML={{ __html: data.customCSS }} />
                )}

                <div className={styles.articleLayout}>
                    <article className={styles.content}>
                        <div className={styles.introBlock}>
                            <p className={styles.introText}>
                                {data.shortDescription}
                            </p>
                        </div>

                        <div className={styles.mainBody}>
                            {data.content && (/<[a-z][\s\S]*>/i.test(data.content) ? (
                                <div dangerouslySetInnerHTML={{ __html: data.content }} />
                            ) : (
                                data.content.split('\r\n').filter(p => p.trim() !== '').map((text, index) => {
                                    if (/^\d+\./.test(text.trim())) {
                                        return <h3 key={index} className={styles.subheading}>{text}</h3>;
                                    }
                                    return <p key={index} className={styles.paragraph}>{text}</p>;
                                })
                            ))}
                        </div>

                        {data.authorQuote && (
                            <motion.blockquote
                                whileInView={{ opacity: 1, x: 0 }}
                                initial={{ opacity: 0, x: -30 }}
                                transition={{ duration: 0.6 }}
                                className={styles.quote}
                            >
                                <span className={styles.quoteMark}>“</span>
                                {data.authorQuote}
                            </motion.blockquote>
                        )}

                        <div className={styles.articleFooter}>
                            <div className={styles.shareSection}>
                                <h4>Share this Article</h4>
                                <div className={styles.shareBtns}>
                                    <button className={styles.socialBtn} onClick={() => handleShare('facebook')}><Facebook size={18} /></button>
                                    <button className={styles.socialBtn} onClick={() => handleShare('twitter')}><Twitter size={18} /></button>
                                    <button className={styles.socialBtn} onClick={() => handleShare('linkedin')}><Linkedin size={18} /></button>
                                    <button className={styles.socialBtn} onClick={() => handleShare('whatsapp')} title="Share on WhatsApp"><MessageCircle size={18} /></button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.commentsList}>
                            <h3 className={styles.subheading}>Comments ({comments?.length || 0})</h3>
                            {isCommentsLoading ? (
                                <p>Loading comments...</p>
                            ) : comments && comments.length > 0 ? (
                                comments.map(comment => (
                                    <div key={comment._id} className={styles.commentItem}>
                                        <div className={styles.commentHeader}>
                                            <span className={styles.commentName}>{comment.name}</span>
                                            <span className={styles.commentDate}>
                                                {new Date(comment.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className={styles.commentContent}>{comment.content}</p>
                                    </div>
                                ))
                            ) : (
                                <div className={styles.noComments}>
                                    No comments yet. Be the first to share your thoughts!
                                </div>
                            )}
                        </div>
                    </article>

                    <aside className={styles.sidebar}>
                        <div className={styles.sidebarWidget}>
                            <h3>Recent Articles</h3>
                            <div className={styles.recentPosts}>
                                {allBlogs.slice(0, 4).filter(b => b._id !== id).map(blog => (
                                    <div key={blog._id} className={styles.miniPost} onClick={() => navigate(`/blog/${blog.slug}`)}>
                                        <div className={styles.miniPostImg}>
                                            <img src={blog.images?.[0] ? `${process.env.REACT_APP_API_URL || "https://api.createchsquads.com"}/${blog.images[0].replace(/\\/g, "/")}` : "/assets/placeholder.png"} alt={blog.title} />
                                        </div>
                                        <div className={styles.miniPostInfo}>
                                            <h4>{blog.title.length > 50 ? blog.title.substring(0, 50) + "..." : blog.title}</h4>
                                            <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.newsletterWidget}>
                            <h3>Growth & Strategy</h3>
                            <p>Get the latest MNC insights delivered directly to your inbox.</p>
                            <form className={styles.miniForm} onSubmit={handleNewsletterSubmit}>
                                <input 
                                    type="email" 
                                    placeholder="Your Email" 
                                    required 
                                    value={subscriberEmail}
                                    onChange={(e) => setSubscriberEmail(e.target.value)}
                                />
                                <button type="submit">Join Now</button>
                            </form>
                        </div>
                    </aside>
                </div>

                <motion.section
                    whileInView={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 50 }}
                    transition={{ duration: 0.8 }}
                    className={styles.replySection}
                >
                    <div className={styles.formBadge}>Join the Conversation</div>
                    <h3 className={styles.replyTitle}>Leave a Response</h3>
                    <p className={styles.replyNote}>
                        Your professional insights are highly valued.
                    </p>

                    <form className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.inputGroup}>
                            <div className={styles.field}>
                                <input 
                                    type="text" 
                                    name="name"
                                    placeholder="Full name*" 
                                    required 
                                    className={styles.input} 
                                    value={commentData.name}
                                    onChange={handleInputChange}
                                />
                                <span className={styles.line}></span>
                            </div>
                            <div className={styles.field}>
                                <input 
                                    type="email" 
                                    name="email"
                                    placeholder="Email address*" 
                                    required 
                                    className={styles.input} 
                                    value={commentData.email}
                                    onChange={handleInputChange}
                                />
                                <span className={styles.line}></span>
                            </div>
                        </div>
                        <div className={styles.field}>
                            <textarea 
                                name="content"
                                placeholder="Share your thoughts*" 
                                className={styles.textarea} 
                                required
                                value={commentData.content}
                                onChange={handleInputChange}
                            ></textarea>
                            <span className={styles.line}></span>
                        </div>
                        {isSubmitted && (
                            <div style={{ color: '#22c55e', fontSize: '1rem', fontWeight: '600', marginBottom: '10px' }}>
                                Your comment has been posted successfully!
                            </div>
                        )}
                        <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                            {isSubmitting ? "Posting..." : "Post Comment"} <ChevronRight size={18} />
                        </button>
                    </form>
                </motion.section>
            </div>
        </div>
    );
};

export default BlogDetail;
